import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getBuild, renderBuild } from '../buildApi.js'
import { useAccess } from '../../../lib/access.jsx'
import { ACTIONS } from '../../../lib/rbac.js'
import { searchChampionships } from '../engine/pickerData.js'
import { styleRows, productRows, presetNeedsVenue, mergeConfig } from './studioConfig.js'
import { mergeSteps } from './steps.js'
import {
  emptyBuild, validateStudio, lockedSummary, stepDone, stepSummary, refLabel, venueContext,
  presentationLineDefault, renderSettled,
} from './buildPayload.js'
import StudioStep from './StudioSteps.jsx'
import PrecedentPanel from './PrecedentPanel.jsx'
import { levelForMode, levelRows, levelUsesSteps, levelUsesBridge } from './levels.js'
import ConceptBrief from './ConceptBrief.jsx'
import BezelDrop from './BezelDrop.jsx'
import { flowSeed } from './flows.js'
import CanvaConnection from './CanvaConnection.jsx'
import StudioResult from './StudioResult.jsx'
import { provenanceNote } from '../gptBridge/bridgeModel.js'
import { stepsFor, trimmedSteps, matrixCoverage, cellFor, STYLE_BY_ID } from './productModel.js'
import styles from './Studio.module.css'

/** The local option lists, for a render with no config prop. */
const FALLBACK_STUDIO_CONFIG = mergeConfig(null)

/**
 * THE ARTWORK STUDIO — the paint-by-numbers frontend for the SR
 * recognition-product builder.
 *
 * EVERY TOGGLE IS CONFIG-DRIVEN. `GET /api/artwork/config` is the authority on
 * the Setting & Output presets, the modes, the views, the scales, the
 * complexity range, the styles that actually have a BEAST, and the script
 * steps. A new preset or a new beast on the backend appears here with no
 * frontend change (`studioConfig.js#mergeConfig`). When /config is unreachable
 * the studio still runs off local defaults and SAYS SO, rather than silently
 * presenting a stale menu as the engine's.
 *
 * ONE STEP AT A TIME, AND IT IS AN ACCORDION. Each step states the next move in
 * one line, carries its own selector and its own "add to the AI script" box,
 * and ticks on the progress rail when answered. Thirteen steps expanded at once
 * is one endless scroll on a phone, so a step opens when it is the one to work
 * on and collapses to a header that still shows its answer. Nothing is
 * autofilled: suggestions are chips the operator clicks.
 *
 * MOBILE FIRST. This gets used on a phone in a gym, not only at a desk. The
 * layout is a single column by default and only becomes a two-column desk
 * layout at 1080px and up; the rail sits BELOW the work on a phone, the primary
 * action is pinned to the bottom of the viewport, and every control is a
 * 44px-plus touch target. Nothing scrolls sideways.
 *
 * THE LOCKED LAYER IS ON SCREEN. The panel lists the exact strings the backend
 * will assert survive into every prompt, the client crest it will reproduce
 * faithfully, and the SR mark that is always on — so the operator can see what
 * is protected before submitting.
 */
/* `config` and `configState` are PROPS now, not state. The page asks
 * `/api/artwork/config` once (`useStudioConfig`) and hands the same answer to
 * the Builder and the hand-off stage — two components fetching separately is
 * two answers that can disagree. The defaults keep this component renderable on
 * its own: a missing config degrades to the local option lists, never to a
 * crash. */
export default function Studio({
  onBuilt,
  serviceTier = null,
  jobContext = null,
  config = FALLBACK_STUDIO_CONFIG,
  configState = 'ready',
} = {}) {
  const { can, why } = useAccess()

  /* ---- build state --------------------------------------------------- */
  const [build, setBuild] = useState(() => { const b = emptyBuild({}); return jobContext ? { ...b, championshipId: jobContext.championshipId || '', keyed: { ...b.keyed, titleTop:jobContext.spec?.titleTop || '', titleBottom:jobContext.spec?.titleBottom || '', year:jobContext.spec?.year || '' } } : b })
  const [school, setSchool] = useState(null)
  const [championship, setChampionship] = useState(null)
  const seeded = useRef(false)

  // Seed the render-mechanics defaults from the engine's own config once it
  // answers — but never over a choice the operator has already made.
  useEffect(() => {
    if (configState === 'loading' || seeded.current) return
    seeded.current = true
    /* THE PRODUCT-BUILDERS HAND-OFF. `/artwork-studio?flow=champ-pendant` used
     * to be read by ConceptGenerator, which has been unrouted for months — so
     * every "Generate concept" button on the Product Builders page landed here
     * and was silently ignored. The param now seeds the product and style, and
     * only ever as a SEED: it is applied once, before the operator has touched
     * anything, and never over a choice already made. */
    const seed = flowSeed(typeof window === 'undefined' ? '' : window.location.search)
    setBuild((b) => ({
      ...b,
      ...(seed.product ? { product: seed.product } : {}),
      ...(seed.style ? { style: seed.style } : {}),
      mode: serviceTier ? 'render' : config.defaultMode || b.mode,
      settingPreset: config.defaultSettingPreset || b.settingPreset,
      scale: config.defaultScale || b.scale,
      complexity: config.defaultComplexity || b.complexity,
      views: (config.defaultViews || []).length ? [...config.defaultViews] : b.views,
    }))
  }, [configState, config, serviceTier])

  const ctl = useMemo(() => ({
    setTop: (key, value) => setBuild((b) => ({ ...b, [key]: value })),
    setSetting: (key, value) => setBuild((b) => ({ ...b, setting: { ...b.setting, [key]: value } })),
    setComponent: (key, value) => setBuild((b) => ({ ...b, components: { ...b.components, [key]: value } })),
    setKeyed: (key, value) => setBuild((b) => ({ ...b, keyed: { ...b.keyed, [key]: value } })),
    setScript: (key, value) => setBuild((b) => ({ ...b, scripts: { ...b.scripts, [key]: value } })),
    setLogoC: (value) => setBuild((b) => ({ ...b, logoC: value })),
    setStones: (patch) => setBuild((b) => ({ ...b, components: { ...b.components, stones: { ...b.components.stones, ...patch } } })),
    setMetal: (patch) => setBuild((b) => ({ ...b, components: { ...b.components, metal: { ...b.components.metal, ...patch } } })),
    setActionBezel: (patch) => setBuild((b) => ({ ...b, components: { ...b.components, actionBezel: { ...b.components.actionBezel, ...patch } } })),
    toggleStone: (field, value) => setBuild((b) => {
      const cur = b.components.stones[field] || []
      const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]
      return { ...b, components: { ...b.components, stones: { ...b.components.stones, [field]: next } } }
    }),
    toggleTeamColor: (value) => setBuild((b) => {
      const cur = b.components.teamColor || []
      const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]
      return { ...b, components: { ...b.components, teamColor: next } }
    }),
    addName: (value) => setBuild((b) => (
      (b.keyed.names || []).includes(value)
        ? b
        : { ...b, keyed: { ...b.keyed, names: [...(b.keyed.names || []), value] } }
    )),
    setName: (i, value) => setBuild((b) => {
      const names = [...(b.keyed.names || [])]
      names[i] = value
      return { ...b, keyed: { ...b.keyed, names } }
    }),
    removeName: (i) => setBuild((b) => ({ ...b, keyed: { ...b.keyed, names: (b.keyed.names || []).filter((_, x) => x !== i) } })),
    setSchool: (s) => setSchool(s),
  }), [])

  /* ---- THE GENERATED-CONCEPT LEVEL -----------------------------------
   * Level 2 replaces the thirteen steps with ONE line. `ConceptBrief` resolves
   * it against the championship and school records and hands back a COMPLETE
   * build plus the receipts saying where every line came from.
   *
   * The composed build is written into the SAME `build` state the steps use, so
   * `buildPayload()`, `validateStudio()`, the locked-layer rail and the submit
   * button are all unchanged and shared. That is the point: a level changes the
   * input path, never the contract — a concept is refused by exactly the same
   * gate a sourced build is, including the product × style cell with no beast.
   * ------------------------------------------------------------------- */
  const [concept, setConcept] = useState(null)

  const onCompose = useCallback((out) => {
    setConcept(out)
    /* ⚠️ THE OPERATOR'S OWN CHOICES SURVIVE SOURCING, and `mode` is the one
     * that matters most. The concept composes onto `emptyBuild(config)`, whose
     * mode is the engine's DEFAULT ('render') — so writing the composed build
     * in wholesale silently flipped the level back to Sourced the instant a
     * championship was picked, and the thirteen steps and their "Step 1 — the
     * bezel logo (C) is required" blocker came back mid-flow. Caught by
     * driving the live page, not by reading this. */
    setBuild((b) => ({ ...out.build, mode: b.mode, product: b.product, style: b.style }))
    if (out.championship) setChampionship(out.championship)
    if (out.school) setSchool(out.school)
  }, [])

  const resetConcept = useCallback(() => {
    setConcept(null)
    setChampionship(null)
    setSchool(null)
    setBuild((b) => ({ ...emptyBuild(config), product: b.product, style: b.style, mode: b.mode }))
  }, [config])

  /* ---- championship context (suggestions only) ----------------------- */
  const context = useMemo(() => ({ school, championship }), [school, championship])

  /* ---- THE PRESENTATION LINE DEFAULTS FROM THE RECORD -----------------
   * Step 13 used to require it by hand although School, Sport, Championship
   * and Year were all already derived. It is filled the moment those records
   * are known and stays fully editable; `autoLine` remembers what we wrote, so
   * once the operator changes it, nothing here touches it again. Clearing it
   * by hand is respected too: this only ever runs when the school or the
   * championship changes, never on a keystroke.
   * -------------------------------------------------------------------- */
  const autoLine = useRef('')
  const lineRef = useRef('')
  lineRef.current = build.keyed.presentationLine
  useEffect(() => {
    const next = presentationLineDefault(lineRef.current, autoLine.current, { championship, school })
    if (next == null) return
    autoLine.current = next
    ctl.setKeyed('presentationLine', next)
  }, [championship, school, ctl])

  function chooseChampionship(ch) {
    setChampionship(ch)
    setBuild((b) => {
      const v = venueContext({ championship: ch, school })
      return {
        ...b,
        championshipId: ch?.id ? String(ch.id) : '',
        // The venue preset SUGGESTS from the championship's sport + region.
        // These two are context for the engine's venue lookup, not keyed type —
        // no rendered string is written here.
        setting: { ...b.setting, sport: b.setting.sport || v.sport, region: b.setting.region || v.region },
      }
    })
  }

  /* ---- steps + validation --------------------------------------------
   * THE FLOW IS TRIMMED TO THE PIECE. The 13 Championship-ring steps are the
   * reference flow; a pendant has no side shanks and no palm-interior band, so
   * those steps are not drawn, not asked for and not posted. `stepsFor`
   * renumbers what survives 1..n, because a flow reading "1, 2, 4, 9" is not
   * paint-by-numbers any more. Order is never changed — a trimmed flow is a
   * SUBSET of the one a rep already knows. */
  const cell = useMemo(() => cellFor(build.product, build.style, config), [build.product, build.style, config])
  const allSteps = useMemo(() => mergeSteps(config, undefined, build.style), [config, build.style])
  const steps = useMemo(
    () => stepsFor(allSteps, build.product, build.style),
    [allSteps, build.product, build.style]
  )
  const trimmed = useMemo(() => trimmedSteps(build.product, build.style), [build.product, build.style])
  const coverage = useMemo(() => matrixCoverage(config), [config])
  const { problems, warnings, payload } = useMemo(
    () => validateStudio(build, config),
    [build, config]
  )
  const locked = useMemo(() => lockedSummary(build), [build])
  const done = useMemo(() => steps.filter((s) => stepDone(build, s)).length, [steps, build])

  /* ---- the accordion --------------------------------------------------
   * The step to work on OPENS on its own; nothing ever closes on its own.
   * Auto-collapsing the step you are typing in the instant it counts as
   * answered is exactly the behavior that makes an accordion infuriating, so
   * this effect only ever adds. Collapsing is the operator's call.
   * -------------------------------------------------------------------- */
  const nextKey = useMemo(
    () => (steps.find((s) => !stepDone(build, s)) || steps[0] || {}).key,
    [steps, build]
  )
  const [expanded, setExpanded] = useState({})
  useEffect(() => {
    if (!nextKey) return
    setExpanded((e) => (e[nextKey] ? e : { ...e, [nextKey]: true }))
  }, [nextKey])

  const toggleStep = useCallback((key) => {
    setExpanded((e) => ({ ...e, [key]: !e[key] }))
  }, [])
  const setAllSteps = useCallback((open) => {
    setExpanded(open ? Object.fromEntries(steps.map((s) => [s.key, true])) : {})
  }, [steps])
  const goToStep = useCallback((key) => {
    setExpanded((e) => ({ ...e, [key]: true }))
    setTimeout(() => {
      document.getElementById(`step-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 30)
  }, [])

  /* ---- submit --------------------------------------------------------- */
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)
  const [submitErr, setSubmitErr] = useState(null)
  const resultRef = useRef(null)

  const mayRender = can(ACTIONS.ARTWORK_BUILD_CREATE)

  const submit = useCallback(async () => {
    if (problems.length || submitting) return
    setSubmitting(true)
    setSubmitErr(null)
    try {
      const res = await renderBuild({ ...payload, ...(jobContext ? { workspaceJobId:jobContext.id } : {}) })
      setResult(res)
      onBuilt?.(res.record)
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
    } catch (err) {
      setSubmitErr({
        message: err?.message || 'The render request failed.',
        problems: err?.body?.problems || err?.problems || null,
      })
    } finally {
      setSubmitting(false)
    }
  }, [problems.length, submitting, payload, onBuilt, jobContext])

  /* ---- WAIT FOR THE IMAGES -------------------------------------------
   * The render endpoint used to do every image call before replying, so a
   * five-view build ran for minutes behind a gateway that gives up in
   * seconds. The user got a 504 and lost the build; the server finished it
   * into a record nobody was watching.
   *
   * The POST now returns as soon as the build is ASSEMBLED, and the images
   * arrive afterwards — so this polls the build record until they land.
   *
   * WHY POLLING AND NOT A SOCKET: the record on the server is the truth
   * either way, and polling survives the tab being closed and reopened,
   * a dropped connection, and a redeploy mid-render. A socket would be
   * fewer requests and one more thing that can silently be disconnected
   * while looking connected.
   *
   * It stops on its own: when renderPending clears, on unmount, and at a
   * hard ceiling so a wedged render cannot poll this tab forever. */
  useEffect(() => {
    const buildId = result?.buildId
    if (!buildId || !result?.renderPending) return

    let alive = true
    let tries = 0
    const MAX_TRIES = 150            // ~7.5 min at 3s — well past a normal render
    const timer = setInterval(async () => {
      if (!alive) return
      if (++tries > MAX_TRIES) {
        clearInterval(timer)
        // Not silence, and not a fake failure either: the build is real and
        // on the server, we simply stopped watching it.
        setSubmitErr({
          message: 'Still rendering after several minutes. The build is saved — open it from History to check on it.',
          problems: null,
        })
        return
      }
      try {
        const res = await getBuild(buildId)
        const rec = res?.record
        if (!alive || !rec) return
        setResult((prev) => (prev && prev.buildId === buildId
          ? { ...prev, images: rec.images || prev.images, record: rec, notes: rec.notes, renderPending: !!rec.renderPending }
          : prev))
        // Settled, not merely "renderPending false": a spend-off build settles
        // with renderPending still true and a note saying why (buildPayload.js).
        if (renderSettled(rec)) {
          clearInterval(timer)
          onBuilt?.(rec)
        }
      } catch {
        // A blip mid-render is not a failed render. Keep waiting; the ceiling
        // above is what ends it.
      }
    }, 3000)

    return () => { alive = false; clearInterval(timer) }
  }, [result?.buildId, result?.renderPending, onBuilt])

  /* ---- render --------------------------------------------------------- */
  const products = productRows(config, build.style)
  const stylesList = styleRows(config, build.product)
  const styleMeta = STYLE_BY_ID[build.style] || null
  const levels = levelRows(config).filter(l => !serviceTier || !l.viaBridge)
  const level = levelForMode(build.mode)
  /* THE LEVEL IS A STRUCTURAL FORK, not a flag: it decides whether the main
   * column draws the thirteen-step spine or the one-line brief. */
  const usesSteps = levelUsesSteps(build.mode)
  /* ⚠️ A SECOND FORK, AND A DEEPER ONE. `usesSteps` chooses between thirteen
   * steps and one line — both of which end at `POST /api/artwork/render`.
   * `usesBridge` says this level does not go to the render engine AT ALL: the
   * Bezel Drop sheet comes back through the GPT bridge. So everything the
   * engine owns — the product/style matrix, the views, the setting preset, the
   * locked-layer rail, the blockers, the Render button — is not merely unused
   * here, it is about a different job, and leaving it on screen would offer a
   * button that 400s on a mode the backend has never heard of. */
  const usesBridge = levelUsesBridge(build.mode)
  /* Is there a real job on screen yet? A championship, a school, or a keyed
   * title — any one of them means the operator is building something specific. */
  const hasJob = !!(championship || school || (build.keyed && build.keyed.titleTop))
  const needsVenue = presetNeedsVenue(config, build.settingPreset)

  return (
    <div className={styles.wrap}>
      {/* ⚠️ THE SECOND TAB BAR IS GONE. This component used to render its own
          `Builder | Build history` row directly under the page's four-tab bar —
          two stacked bars, six destinations, and on a phone no work visible at
          all above the fold. Build history is not a sibling of building; it is
          what you do AFTER, so it is stage 3 (Hand off) on the page's one
          stage rail. */}
        <div className={styles.layout}>
          {/* ------------------------- left rail ------------------------- */}
          <aside className={styles.rail}>
            {/* PROGRESS IS A STEP IDEA. A generated concept has one input, so
                "4 of 13" would be counting a flow the operator is not in; the
                receipt below is what tells them where the build stands. */}
            {usesBridge && (
              <section className={`card ${styles.railCard}`}>
                <div className="field-label">Level 3 — Bezel Drop</div>
                <p className={styles.note}>{level.inputBurden}</p>
                {/* The departure from SHARED_GUARANTEES, in the rail as well as
                    in the work, because the rail is what stays on screen. */}
                <p className={styles.note}>{level.departs}</p>
              </section>
            )}

            {!usesSteps && !usesBridge && (
              <section className={`card ${styles.railCard}`}>
                <div className="field-label">Level 2 — generated concept</div>
                <p className={styles.note}>{level.inputBurden}</p>
                <p className={styles.note}>
                  {concept
                    ? `${concept.receipts.length} line${concept.receipts.length === 1 ? '' : 's'} sourced from the records. Check them, correct anything wrong, then render.`
                    : 'Describe the job in one line and the system sources the rest.'}
                </p>
              </section>
            )}

            {usesSteps && (
            <section className={`card ${styles.railCard}`}>
              <div className={styles.railHead}>
                <span className="field-label">Progress</span>
                <span className={styles.progressCount}>{done} of {steps.length}</span>
              </div>
              <div className={styles.progressBar}>
                <span style={{ width: `${Math.round((done / steps.length) * 100)}%` }} />
              </div>
              <ol className={styles.railSteps}>
                {steps.map((s) => {
                  const ok = stepDone(build, s)
                  return (
                    <li key={s.key}>
                      {/* Jumping to a COLLAPSED step and landing on a closed
                          header is a dead end, so the jump opens it too. */}
                      <button
                        type="button"
                        className={`${styles.railStep} ${ok ? styles.railDone : ''}`}
                        onClick={() => goToStep(s.key)}
                      >
                        <span className={styles.railNum}>{ok ? '✓' : s.n}</span>
                        <span>{s.title}</span>
                        {s.required && !ok && <span className={styles.req}>required</span>}
                      </button>
                    </li>
                  )
                })}
              </ol>
            </section>
            )}

            {!usesBridge && (<>
            <section className={`card ${styles.railCard}`}>
              <div className="field-label">Locked layer — the AI never touches these</div>
              <ul className={styles.lockList}>
                {locked.marks.map((m) => (
                  <li key={m.label} className={m.ok ? styles.lockOk : styles.lockMissing}>
                    <strong>{m.label}</strong>
                    <span>{m.detail}</span>
                  </li>
                ))}
              </ul>
              {!!locked.strings.length && (
                <>
                  <div className="field-label" style={{ marginTop: 10 }}>Exact keyed type</div>
                  <ul className={styles.lockStrings}>
                    {locked.strings.map((s, i) => (
                      <li key={s.label + i}>
                        <span className={styles.lockLabel}>{s.label}</span>
                        <code>{s.value}</code>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>

            {/* Directly under the locked layer on purpose: one card says what
                the AI may never touch, the next says what a PRIOR JOB may
                never lend. Same fence, two sources. */}
            {/* ⚠️ NO JOB, NO PRECEDENT. Brian, on the live site: *"generated
                concept should not reference a high school."* One of the two
                sources was this panel: with nothing sourced yet it still fell
                back to the nearest thing the registry holds and rendered
                "SR-REF-000001 · PALO DURO HIGH SCHOOL FOOTBALL 2026", the Dons
                mascot and the Palo Duro wording — another account's identity,
                on screen, before the operator had typed a word. A precedent is
                an answer to "what is this job like"; with no job there is no
                question, so it does not render. */}
            {hasJob && (
              <PrecedentPanel build={build} championship={championship} school={school} />
            )}

            <section className={`card ${styles.railCard}`}>
              <div className="field-label">This build</div>
              <ul className={styles.summary}>
                <SumRow label="Product / style" value={`${build.product} · ${build.style}`} />
                <SumRow label="Mode" value={build.mode} />
                <SumRow label="Setting & output" value={build.settingPreset} />
                <SumRow label="Bezel logo C" value={refLabel(build.logoC)} />
                <SumRow label="Bezel & top" value={refLabel(build.components.bezel)} />
                <SumRow label="Girdle" value={build.components.girdle} />
                <SumRow label="Shanks" value={[refLabel(build.components.shank1), refLabel(build.components.shank2)].filter(Boolean).join(' · ')} />
                <SumRow label="Stones" value={[(build.components.stones.shapes || []).join(', '), (build.components.stones.setting || []).join(', '), build.components.stones.package].filter(Boolean).join(' · ')} />
                <SumRow label="Metal" value={[build.components.metal.tone, build.components.metal.finish, build.components.metal.vibrantiumId].filter(Boolean).join(' · ')} />
                <SumRow label="Scale / complexity" value={`${build.scale} · ${build.complexity}`} />
                <SumRow label="Views" value={`${build.views.length} of ${(config.views || []).length}`} />
                <SumRow label="Script boxes filled" value={String(Object.values(build.scripts).filter((v) => String(v || '').trim()).length)} />
              </ul>
            </section>
            </>)}
          </aside>

          {/* ------------------------- main ------------------------------ */}
          <div className={styles.main}>
            {/* On a phone the rail sits BELOW the work, so progress comes with
                the work instead of being a screenful to scroll past. */}
            {usesSteps && (
              <div className={styles.mobileProgress}>
                <div className={styles.railHead}>
                  <span className="field-label">Progress</span>
                  <span className={styles.progressCount}>{done} of {steps.length}</span>
                </div>
                <div className={styles.progressBar}>
                  <span style={{ width: `${Math.round((done / steps.length) * 100)}%` }} />
                </div>
              </div>
            )}

            <CanvaConnection />

            {configState === 'fallback' && (
              <p className={styles.warnNote}>
                <strong>Running on local defaults.</strong> <code>GET /api/artwork/config</code> was unreachable, so these
                toggles are this build&apos;s copy of the engine&apos;s options rather than the engine&apos;s own answer. They may be
                stale — reload once the API is reachable.
              </p>
            )}

            {/* -------- top controls -------- */}
            <section className={`card ${styles.controls}`}>
              {/* THE MATRIX, ON SCREEN. Product and style are two axes of one
                  decision, and what decides a cell is neither axis on its own —
                  it is whether the ENGINE has a beast that renders that piece.
                  So each chip carries the cell's own verdict for the other axis
                  as currently selected, and a blocked chip states the missing
                  artefact instead of a flat "coming". */}
              {/* A Bezel Drop has no product/style cell to satisfy: it is one
                  mark across the HOUSINGS SR already has. Offering the matrix
                  here would be offering a choice that changes nothing. */}
              {!usesBridge && (<>
              <Control
                label="Product"
                hint={`What SR is making. ${coverage.live} of ${coverage.total} product × style combinations can render today — every other cell is waiting on an engine-side prompt, named on the chip.`}
              >
                <div className={styles.chipRow}>
                  {products.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`${styles.opt} ${build.product === p.id ? styles.optOn : ''} ${p.available ? '' : styles.optOff}`}
                      disabled={!p.available}
                      title={p.available ? p.what : p.reason}
                      onClick={() => ctl.setTop('product', p.id)}
                    >
                      {p.label}
                      {!p.available && <span className={styles.soon}>coming</span>}
                    </button>
                  ))}
                </div>
                {!cell.available && (
                  <p className={styles.blockedNote}>
                    <strong>{blockedLabel(cell)}</strong> {cell.reason}
                  </p>
                )}
              </Control>

              <Control label="Style" hint="What KIND of piece it is. A style is a beast prompt — the engine refuses a style it has no beast for, so one with none reads as coming rather than failing at render time.">
                <div className={styles.chipRow}>
                  {stylesList.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`${styles.opt} ${build.style === s.id ? styles.optOn : ''} ${s.available ? '' : styles.optOff}`}
                      disabled={!s.available}
                      title={s.available ? (s.beastLabel || s.what) : s.reason}
                      onClick={() => ctl.setTop('style', s.id)}
                    >
                      {s.label}
                      {!s.available && <span className={styles.soon}>coming</span>}
                    </button>
                  ))}
                </div>
                {styleMeta && styleMeta.confirmed === false && (
                  <p className={styles.note}>
                    <strong>Provisional step set.</strong> Master Spec Section 12 leaves the per-style steps for this style
                    open, so the flow below is the studio&apos;s reading of the piece, not the spec&apos;s answer. The engine is the
                    authority: the day <code>/config</code> declares this style&apos;s own script steps, they win here with no
                    change to the studio.
                  </p>
                )}
              </Control>
              </>)}

              {/* ⚠️ THE LEVEL IS AN INPUT BURDEN, and this chip now changes
                  what is on screen rather than one paragraph of a prompt.
                  Until 2026-09-09 it mapped 1:1 onto the engine's
                  `mode: render | generate`, which — measured against the real
                  assembler — moves 2 sections out of 18 and leaves the beast,
                  the components and the locked layer byte-identical, while
                  `validateStudio()` demanded the same thirteen steps either
                  way. Two levels that differ by a word are one level.
                  `levels.js` carries the measurement. */}
              <Control label={serviceTier ? "Design input" : "Level"} hint="Choose guided components or a sourced brief. Every output is a draft that needs quality review.">
                <div className={styles.chipRow}>
                  {levels.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      className={`${styles.opt} ${build.mode === l.mode ? styles.optOn : ''} ${l.available ? '' : styles.optOff}`}
                      disabled={!l.available}
                      title={l.available ? l.tagline : 'The engine does not accept this mode — it would refuse the build.'}
                      onClick={() => ctl.setTop('mode', l.mode)}
                    >
                      {serviceTier ? (l.mode === 'render' ? 'Guided components' : 'Sourced brief') : `${l.n} · ${l.label}`}
                      {!l.available && <span className={styles.soon}>coming</span>}
                    </button>
                  ))}
                </div>
                <p className={styles.levelBurden}>{level.inputBurden}</p>
                {!serviceTier && (<>
                <p className={styles.note}>{level.what}</p>
                {/* ⚠️ A LEVEL THAT DOES NOT MEET THE SHARED GUARANTEES SAYS SO
                    HERE, in the same control that offered it. Listing four
                    guarantees and staying silent about the one it breaks is how
                    a drawn-by-model sheet gets mistaken for artwork. */}
                {level.sharedSpec === false && level.departs && (
                  <p className={styles.warnNote}><strong>Not the same spec.</strong> {level.departs}</p>
                )}
                <ul className={styles.levelGuarantees}>
                  {level.guarantees.map((g) => <li key={g}>{g}</li>)}
                </ul>
                </>)}
              </Control>

              {!usesBridge && (<>
              <Control label="Setting & output" hint="Layer 0 — the environment and the quality bar. It leads every render and changes the scene without touching the ring.">
                <div className={styles.chipRow}>
                  {(config.settingPresets || []).map((p) => (
                    <button key={p.id} type="button" className={`${styles.opt} ${build.settingPreset === p.id ? styles.optOn : ''}`} onClick={() => ctl.setTop('settingPreset', p.id)}>
                      {p.label}
                    </button>
                  ))}
                </div>
                {needsVenue && (
                  <div className={styles.venue}>
                    <p className={styles.note}>
                      Suggested from the championship&apos;s sport and region — confirm or override. Leave the venue blank and
                      the engine picks one from the sport.
                    </p>
                    <div className={styles.three}>
                      <input className="input" placeholder="Sport" value={build.setting.sport} onChange={(e) => ctl.setSetting('sport', e.target.value)} />
                      <input className="input" placeholder="Region" value={build.setting.region} onChange={(e) => ctl.setSetting('region', e.target.value)} />
                      <input className="input" placeholder="Venue (overrides both)" value={build.setting.venue} onChange={(e) => ctl.setSetting('venue', e.target.value)} />
                    </div>
                  </div>
                )}
              </Control>

              <Control label="Views to render" hint="Every build renders a SET — one image call per view.">
                <div className={styles.chipRow}>
                  {(config.views || []).map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className={`${styles.opt} ${build.views.includes(v.id) ? styles.optOn : ''}`}
                      onClick={() => ctl.setTop('views', build.views.includes(v.id) ? build.views.filter((x) => x !== v.id) : [...build.views, v.id])}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </Control>

              <Control label="Championship (optional)" hint="Attaches the owned renders to the record, and is where the suggestions come from.">
                <ChampionshipField value={championship} onChange={chooseChampionship} />
              </Control>
              </>)}
            </section>

            {/* -------- LEVEL 3 · the Bezel Drop sheet --------
                Its own surface, not a variant of the brief and not a shortened
                wizard. Everything it needs is inside it. */}
            {usesBridge && <BezelDrop />}

            {/* -------- LEVEL 2 · the one-line brief -------- */}
            {!usesSteps && !usesBridge && (
              <ConceptBrief
                base={emptyBuild(config)}
                composed={concept}
                onCompose={onCompose}
                onReset={resetConcept}
              />
            )}

            {/* -------- LEVEL 1 · the steps -------- */}
            {usesSteps && (<>
            <div className={styles.stepsHead}>
              <span className="field-label">The {steps.length} steps</span>
              <div className={styles.stepsHeadActions}>
                <button type="button" className={styles.linkBtn} onClick={() => setAllSteps(true)}>Expand all</button>
                <button type="button" className={styles.linkBtn} onClick={() => setAllSteps(false)}>Collapse all</button>
              </div>
            </div>

            {/* A SHORTER FLOW IS A DECISION, SO IT SAYS SO. Without this, a rep
                who knows the 13-step ring flow and sees eight steps on a pendant
                has no way to tell whether the studio trimmed them or lost them. */}
            {trimmed.length > 0 && (
              <p className={styles.trimNote}>
                Trimmed to the piece — {trimmed.length} of the 13 Championship-ring steps do not apply here:{' '}
                {trimmed.map((t, i) => (
                  <span key={t.key}>
                    {i > 0 && ', '}
                    <strong>{t.title}</strong> ({t.reason.replace(/\.$/, '')})
                  </span>
                ))}
                . Nothing you keyed for them is posted.
              </p>
            )}

            {steps.map((s) => {
              const ok = stepDone(build, s)
              const open = !!expanded[s.key]
              const summary = stepSummary(build, s)
              return (
                <section key={s.key} className={`card ${styles.stepCard}`} id={`step-${s.key}`}>
                  <button
                    type="button"
                    className={styles.stepToggle}
                    aria-expanded={open}
                    aria-controls={`steppanel-${s.key}`}
                    onClick={() => toggleStep(s.key)}
                  >
                    <span className={`${styles.stepNum} ${ok ? styles.stepNumDone : ''}`} aria-hidden>
                      {ok ? '✓' : s.n}
                    </span>
                    <span className={styles.stepToggleText}>
                      <span className={styles.stepToggleTitle}>
                        {s.title}
                        {s.required && !ok && <span className={styles.req}>required</span>}
                      </span>
                      <span className={ok ? styles.stepToggleVal : styles.stepToggleNext}>
                        {ok ? (summary || 'Answered') : s.next}
                      </span>
                    </span>
                    <span className={`${styles.chev} ${open ? styles.chevOpen : ''}`} aria-hidden>▾</span>
                  </button>
                  {open && (
                    <div id={`steppanel-${s.key}`} className={styles.stepPanel}>
                      <StudioStep step={s} state={build} ctl={ctl} config={config} context={context} />
                    </div>
                  )}
                </section>
              )
            })}
            </>)}

            {/* -------- submit --------
                ⚠️ HIDDEN ON A BRIDGE LEVEL, and not merely because it is
                irrelevant. `build.mode` is 'bezeldrop', which the render engine
                has never heard of, so this button would post a mode the backend
                refuses and hand a 400 to whoever pressed it. A control that
                cannot succeed is worse than a missing one. */}
            {!usesBridge && (<>
            <section className={`card ${styles.submit}`} ref={resultRef}>
              {problems.length > 0 && (
                <div className={styles.blockers}>
                  <div className="field-label">Blocked — the engine would refuse this build</div>
                  <ul>{problems.map((p) => <li key={p}>{p}</li>)}</ul>
                </div>
              )}
              {warnings.length > 0 && (
                <div className={styles.warnings}>
                  <div className="field-label">Worth a look — not blocking</div>
                  <ul>{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
                </div>
              )}

              {/* ⚠️ THE PRIMARY BUTTON NAMES THE ROUTE IT ACTUALLY TAKES.
                *
                * Brian, twice: levels 1 and 2 are a GPT path. They were not.
                * The button said "Render 5 views", called the metered
                * /api/artwork/render, and with SR_ALLOW_IMAGE_SPEND false came
                * back with prompts and an empty frame — and the GPT panel was
                * something you only met AFTER that disappointment. The first
                * thing anyone presses produced nothing.
                *
                * The endpoint is unchanged and spend is untouched: it is the
                * ONLY thing that assembles a prompt (the backend owns
                * `buildPrompt`; there is no client-side assembler and writing
                * one would drift from the engine). What changes is where the
                * rep is sent next and what the button promises. With no image
                * key in the environment the press assembles the prompts and
                * hands straight to the GPT, which is where the picture comes
                * from — so the button says so.
                *
                * When an image key IS present the engine really does draw, and
                * the old wording is correct again. The label follows the
                * environment rather than a guess. */}
              {config.renderReady === false && (
                <p className={styles.note}>
                  {config.renderHoldReason || 'Image rendering is unavailable or switched off. The engine assembles prompts but does not draw.'}
                  This button writes the build record and takes you to your GPT, which is where the artwork comes from.
                  Nothing here spends on the API.
                </p>
              )}

              <div className={styles.submitRow}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!!problems.length || submitting || !mayRender}
                  onClick={submit}
                >
                  {submitting
                    ? (config.renderReady === false ? 'Assembling the prompts…' : 'Assembling and rendering…')
                    : config.renderReady === false
                      ? `Render ${build.views.length} view${build.views.length === 1 ? '' : 's'} in my GPT`
                      : `Render ${build.views.length} view${build.views.length === 1 ? '' : 's'}`}
                </button>
                {!mayRender && <span className={styles.note}>{why(ACTIONS.ARTWORK_BUILD_CREATE) || 'You cannot create a build.'}</span>}
                <span className={styles.note}>
                  The build record is written before the images, and every render lands as a <strong>draft</strong>.
                </span>
              </div>

              {submitErr && (
                <div className={styles.errorCard}>
                  <p>{submitErr.message}</p>
                  {!!submitErr.problems?.length && (
                    <ul>{submitErr.problems.map((p) => <li key={p}>{p}</li>)}</ul>
                  )}
                </div>
              )}
            </section>

            {result && (
              <StudioResult
                result={result}
                statuses={config.statuses}
                onRecord={(rec) => setResult((r) => ({ ...r, record: rec }))}
                /* The build record the provenance note came back on carries the
                   [gpt-bridge] history line, so the view reads it from there.
                   If the note was refused, add the line locally so the image
                   still shows this session; the file name is the durable copy. */
                onGptImage={(img) => setResult((r) => {
                  if (!r) return r
                  if (img.record) return { ...r, record: img.record }
                  const rec = r.record || {}
                  const line = provenanceNote({ view: img.view, fileId: img.fileId, url: img.url, route: img.route, at: img.at, gptUrl: img.gpt })
                  return { ...r, record: { ...rec, history: [...(rec.history || []), { at: img.at, note: line, local: true }] } }
                })}
              />
            )}
            </>)}
          </div>

          {/* THE PRIMARY ACTION, PINNED. On a phone the Render button is
              thirteen steps down the page; a build you cannot reach is a build
              that does not happen. It states why it is disabled rather than
              just being dimmed. Desktop keeps the in-flow submit card above and
              hides this. Same rule as the submit card: on a bridge level it
              would post a mode the engine refuses, so it is not drawn. */}
          {!usesBridge && (
          <div className={styles.stickyBar}>
            <div className={styles.stickyMeta}>
              <strong>{done} of {steps.length}</strong>
              <span>{problems.length ? `${problems.length} blocking` : 'ready'}</span>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!!problems.length || submitting || !mayRender}
              onClick={submit}
            >
              {/* The sticky bar is the button most people actually press,
                * because it follows them down the page. It must promise
                * exactly what the one in the card promises. */}
              {submitting
                ? (config.renderReady === false ? 'Assembling…' : 'Rendering…')
                : config.renderReady === false
                  ? `Render ${build.views.length} view${build.views.length === 1 ? '' : 's'} in my GPT`
                  : `Render ${build.views.length} view${build.views.length === 1 ? '' : 's'}`}
            </button>
          </div>
          )}
        </div>
    </div>
  )
}

/** What KIND of gap a blocked cell has, in three words, before the reason. */
function blockedLabel(cell) {
  if (cell.blocked === 'not-renderable') return 'Not a jewelry piece.'
  if (cell.blocked === 'beast-product') return 'The beast is a ring prompt.'
  return 'No beast for this style yet.'
}

function Control({ label, hint, children }) {
  return (
    <div className={styles.control}>
      <div className="field-label">{label}</div>
      {children}
      {hint && <p className={styles.note}>{hint}</p>}
    </div>
  )
}

function SumRow({ label, value }) {
  return (
    <li className={styles.sumRow}>
      <span>{label}</span>
      <span className={value ? styles.sumVal : styles.sumEmpty}>{value || 'not set'}</span>
    </li>
  )
}

/** Live championship autocomplete — API-first, demo fallback (pickerData.js). */
function ChampionshipField({ value, onChange }) {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState([])
  const abortRef = useRef(null)

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setRows([]); return undefined }
    const t = setTimeout(async () => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      try {
        setRows(await searchChampionships(term, ctrl.signal))
      } catch (err) {
        if (err?.name !== 'AbortError') setRows([])
      }
    }, 260)
    return () => clearTimeout(t)
  }, [q])

  useEffect(() => () => abortRef.current?.abort(), [])

  if (value) {
    return (
      <div className={styles.pickerHead}>
        <div className={styles.pickerValue}>
          <span className={styles.pickerChosen}>{value.name}</span>
          <span className={styles.pickerSrc}>{[value.sport, value.state, value.year].filter(Boolean).join(' · ')}</span>
        </div>
        <button type="button" className={styles.linkBtn} onClick={() => onChange(null)}>Clear</button>
      </div>
    )
  }

  return (
    <>
      <input className="input" placeholder="Search championships" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
      {!!rows.length && (
        <ul className={styles.schoolList}>
          {rows.map((r) => (
            <li key={r.id}>
              <button type="button" className={styles.schoolRow} onClick={() => { onChange(r); setQ(''); setRows([]) }}>
                <span className={styles.schoolText}>
                  <span className={styles.schoolName}>{r.name}</span>
                  <span className={styles.schoolLoc}>{[r.sport, r.level, r.state, r.year].filter(Boolean).join(' · ')}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
